/**
 * Solicitação de cirurgia/procedimento ESTRUTURADA.
 *
 * Antes o pedido cirúrgico era só um blob de texto (`content.text`) — dava
 * pra imprimir, mas não pra reabrir num editor de campos nem reaproveitar,
 * como já acontecia com a receita de óculos (`content.od/oe`) e o pedido de
 * exames (`content.exams`). Aqui está o shape que fecha essa lacuna.
 *
 * Contrato de gravação (`Prescription.content`, type `SURGERY`):
 *   {
 *     subtype: 'CIRURGIA',
 *     procedures: SurgeryProcedureItem[],  // estrutura (novo)
 *     notes: string,                       // texto livre do médico
 *     text: string,                        // serialização — SEMPRE gravada
 *   }
 *
 * O `text` continua sendo a fonte da impressão, do PDF, do contexto da IA e
 * do portal, então nada disso precisou mudar. Documento antigo (só `text`)
 * volta a abrir como texto puro — `parseSurgeryContent` devolve `null` e o
 * chamador cai no editor de texto de sempre. Não há migração.
 */

export type SurgeryEye = 'OD' | 'OE' | 'AO';

export const SURGERY_EYE_LABEL: Record<SurgeryEye, string> = {
  OD: 'Olho direito (OD)',
  OE: 'Olho esquerdo (OE)',
  AO: 'Ambos os olhos (AO)',
};

/**
 * UM procedimento do pedido. Olho e prazo são POR PROCEDIMENTO — o mesmo
 * pedido pode levar pterígio OD pra daqui a 2 meses e catarata OE pra daqui
 * a 6.
 */
export interface SurgeryProcedureItem {
  /** Id local, só pra chave de lista e edição estável. */
  id: string;
  /** Nome do procedimento (padronizado pelo checklist ou digitado). */
  name: string;
  eye: SurgeryEye;
  /** Motivo do agendamento (lista da clínica). Ex.: "Realizar cirurgia". */
  reason?: string;
  /** Prazo no formato 'YYYY-MM'. Vazio = sem prazo definido. */
  deadlineMonth?: string;
  cid?: string;
  /** Orientações — nasce da nota padrão do procedimento, editável aqui. */
  note?: string;
}

/** Id local previsível o bastante pra lista, sem depender de crypto. */
export function newProcedureId(): string {
  return `proc-${Date.now().toString(36)}-${Math.floor(Math.random() * 1e6).toString(36)}`;
}

/** 'YYYY-MM' → "julho de 2027" (pt-BR). Vazio devolve ''. */
export function isoMonthToBR(iso: string | undefined): string {
  if (!iso) return '';
  const [y, m] = iso.split('-').map((n) => parseInt(n, 10));
  if (!Number.isFinite(y) || !Number.isFinite(m)) return '';
  return new Date(y, m - 1, 1).toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
}

/** 'YYYY-MM' → epoch ms da meia-noite do dia 1 (timezone -03:00). */
export function isoMonthToFirstDayMs(iso: string): number {
  return new Date(`${iso}-01T00:00:00-03:00`).getTime();
}

/** epoch ms → 'YYYY-MM' em America/Sao_Paulo. */
export function msToIsoMonth(ms: number): string {
  return new Date(ms).toLocaleDateString('sv-SE', { timeZone: 'America/Sao_Paulo' }).slice(0, 7);
}

/** Adiciona N meses a um ISO 'YYYY-MM'. */
export function addMonthsIso(iso: string, months: number): string {
  const [y, m] = iso.split('-').map((n) => parseInt(n, 10));
  const d = new Date(y, m - 1 + months, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

/** "YYYY-MM" do mês atual em America/Sao_Paulo. */
export function thisMonthIso(): string {
  return msToIsoMonth(Date.now());
}

/** O prazo mais próximo entre os procedimentos (epoch ms), ou null. */
export function earliestDeadlineMs(procedures: SurgeryProcedureItem[]): number | null {
  const stamps = procedures
    .map((p) => (p.deadlineMonth ? isoMonthToFirstDayMs(p.deadlineMonth) : Number.NaN))
    .filter((n) => Number.isFinite(n));
  return stamps.length > 0 ? Math.min(...stamps) : null;
}

/** Bloco de texto de UM procedimento — o que vai pro impresso. */
function serializeProcedure(p: SurgeryProcedureItem): string {
  const name = p.name.trim();
  if (!name) return '';
  const lines = [`Procedimento solicitado: ${name} (${p.eye}).`];
  const prazo = isoMonthToBR(p.deadlineMonth);
  if (p.reason && prazo) lines.push(`${p.reason} ${p.eye} até ${prazo}.`);
  else if (p.reason) lines.push(`${p.reason} ${p.eye}.`);
  else if (prazo) lines.push(`Prazo: até ${prazo}.`);
  if (p.cid?.trim()) lines.push(`CID-10: ${p.cid.trim()}`);
  if (p.note?.trim()) lines.push(p.note.trim());
  return lines.join('\n');
}

/**
 * Texto efetivo do pedido: procedimentos estruturados (um bloco cada) e,
 * por último, o texto livre que o médico escreveu. É este texto que é
 * impresso, salvo em `content.text` e mandado pra IA.
 */
export function serializeSurgeryRequest(
  procedures: SurgeryProcedureItem[],
  freeText: string,
): string {
  const blocks = procedures.map(serializeProcedure).filter(Boolean);
  const tail = freeText.trim();
  if (tail) blocks.push(tail);
  return blocks.join('\n\n');
}

/** Um pedido tem conteúdo se há procedimento nomeado ou texto livre. */
export function hasSurgeryContent(
  procedures: SurgeryProcedureItem[],
  freeText: string,
): boolean {
  return procedures.some((p) => p.name.trim()) || freeText.trim().length > 0;
}

function isEye(v: unknown): v is SurgeryEye {
  return v === 'OD' || v === 'OE' || v === 'AO';
}

/**
 * Lê o `content` de um SURGERY salvo. Devolve `null` quando o documento é
 * antigo (sem `procedures`) — o chamador então abre o editor de texto de
 * sempre, sem migrar nada.
 */
export function parseSurgeryContent(
  content: unknown,
): { procedures: SurgeryProcedureItem[]; notes: string } | null {
  if (!content || typeof content !== 'object') return null;
  const c = content as Record<string, unknown>;
  if (!Array.isArray(c.procedures)) return null;
  const procedures: SurgeryProcedureItem[] = [];
  for (const raw of c.procedures) {
    if (!raw || typeof raw !== 'object') continue;
    const r = raw as Record<string, unknown>;
    const name = typeof r.name === 'string' ? r.name : '';
    if (!name.trim()) continue;
    procedures.push({
      id: typeof r.id === 'string' && r.id ? r.id : newProcedureId(),
      name,
      eye: isEye(r.eye) ? r.eye : 'OD',
      reason: typeof r.reason === 'string' ? r.reason : undefined,
      deadlineMonth: typeof r.deadlineMonth === 'string' ? r.deadlineMonth : undefined,
      cid: typeof r.cid === 'string' ? r.cid : undefined,
      note: typeof r.note === 'string' ? r.note : undefined,
    });
  }
  const notes = typeof c.notes === 'string' ? c.notes : '';
  return { procedures, notes };
}

/** Só os campos que vão pro Firestore (sem `undefined`, que ele recusa). */
export function procedureToFirestore(p: SurgeryProcedureItem): Record<string, string> {
  const out: Record<string, string> = { id: p.id, name: p.name.trim(), eye: p.eye };
  if (p.reason?.trim()) out.reason = p.reason.trim();
  if (p.deadlineMonth) out.deadlineMonth = p.deadlineMonth;
  if (p.cid?.trim()) out.cid = p.cid.trim();
  if (p.note?.trim()) out.note = p.note.trim();
  return out;
}
